import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import type { Position } from '@/types/finance-v3';

type ISODate = string;
type ISODateTime = string;
type MatchReportStatus = string;

/** One line on a client-visible invoice, as the v1 client-portal endpoint returns it. */
export interface ClientPortalInvoiceItem {
	invoiceBatchItemId: number | null;
	matchReportNumber: number | null;
	matchNumber: number | null;
	description: string | null;
	lineType: string | null;
	position: Position | null;
	quantity: number | null;
	unitAmount: number;
	amount: number;
	sortOrder: number | null;
	credited: boolean;
}

const CLIENT_SURFACE_DASHBOARD_TTL_MS = 120_000;
const CLIENT_SURFACE_DETAIL_TTL_MS = 120_000;
export const CLIENT_PORTAL_ACTIVATION_STARTED_AT_KEY = 'client_portal_activation_started_at';
const CLIENT_PORTAL_ACTIVATION_GRACE_MS = 3_000;

function wait(durationMs: number): Promise<void> {
	return new Promise((resolve) => window.setTimeout(resolve, durationMs));
}

async function waitForRecentlyActivatedClientAccess(): Promise<void> {
	const activatedAt = Number(sessionStorage.getItem(CLIENT_PORTAL_ACTIVATION_STARTED_AT_KEY));
	if (!Number.isFinite(activatedAt) || activatedAt <= 0) {
		return;
	}

	const waitTime = CLIENT_PORTAL_ACTIVATION_GRACE_MS - (Date.now() - activatedAt);
	if (waitTime > 0) {
		await wait(waitTime);
	}
	sessionStorage.removeItem(CLIENT_PORTAL_ACTIVATION_STARTED_AT_KEY);
}

export interface ClientPortalProfile {
	clientNumber?: number | null;
	clientName?: string | null;
	clientShortName?: string | null;
	groupNumber?: number | null;
	groupName?: string | null;
	accessStatus?: string | null;
	accessRole?: string | null;
	canViewInvoices?: boolean | null;
	canPayInvoices?: boolean | null;
	canViewMatches?: boolean | null;
	stripeBillingCustomerLinked?: boolean | null;
}

export interface ClientPortalBillingPortalSession {
	url?: string | null;
}

export interface ClientPortalInvoice {
	batchId: number | string;
	invoiceNumber?: string | null;
	invoiceDate?: string | null;
	dueDate?: string | null;
	reference?: string | null;
	status?: string | null;
	stripeInvoiceStatus?: string | null;
	invoicedAmount?: number | string | null;
	receivedAmount?: number | string | null;
	balanceDue?: number | string | null;
	hostedInvoiceUrl?: string | null;
	invoicePdfUrl?: string | null;
	createdOn?: string | null;
}

export interface ClientPortalInvoiceReport {
	reportNumber: number | null;
	matchNumber: number | null;
	matchDate: ISODate | null;
	matchTime: string | null;
	homeTeam: string | null;
	awayTeam: string | null;
	field: string | null;
	officialName: string | null;
	position: Position | null;
	status: MatchReportStatus | null;
	officialFee: number | null;
	travelFee: number | null;
	assignorFee: number | null;
	groupFee: number | null;
	totalAmount: number | null;
}

export interface ClientPortalInvoiceDetail {
	batchId: string | null;
	invoiceNumber: string | null;
	groupNumber: number | null;
	groupName: string | null;
	clientNumber: number | null;
	clientName: string | null;
	payPeriodLabel: string | null;
	invoiceDate: ISODate | null;
	dueDate: ISODate | null;
	billingStartDate: ISODate | null;
	billingEndDate: ISODate | null;
	paidDate: ISODate | null;
	reference: string | null;
	memo: string | null;
	status: string | null;
	stripeInvoiceStatus: string | null;
	stripeHostedInvoiceUrl: string | null;
	stripeInvoicePdfUrl: string | null;
	invoicedAmount: number;
	subtotalAmount: number;
	receivedAmount: number;
	balanceDue: number;
	reportCount: number;
	createdOn: ISODateTime | null;
	updatedOn: ISODateTime | null;
	stripeFinalizedOn: ISODateTime | null;
	stripeSentOn: ISODateTime | null;
	stripePaidOn: ISODateTime | null;
	items: ClientPortalInvoiceItem[];
	reports: ClientPortalInvoiceReport[];
}

export interface ClientPortalMatch {
	matchNumber: number | string;
	groupMatchNumber?: number | string | null;
	homeTeam?: string | null;
	awayTeam?: string | null;
	matchDate?: string | null;
	matchTime?: string | null;
	field?: string | null;
	locationName?: string | null;
	league?: string | null;
	division?: string | null;
	level?: string | null;
	season?: string | null;
	status?: string | null;
	covered?: boolean | null;
	openSlots?: number | null;
	assignedSlots?: number | null;
	totalTrackedSlots?: number | null;
	matchReportFiled?: boolean | null;
	matchReportNumber?: number | string | null;
	acceptReport?: string | null;
}

export interface ClientPortalMatchReport {
	reportNumber: number | null;
	matchNumber: number | null;
	groupMatchNumber: number | null;
	homeTeam: string | null;
	awayTeam: string | null;
	homeTeamScore: number | null;
	awayTeamScore: number | null;
	cautions: number | null;
	sendOffs: number | null;
	homeTeamCoaches: number | null;
	homeTeamCoachesReason: string | null;
	homeTeamPlayers: number | null;
	homeTeamPlayersReason: string | null;
	homeTeamSpectators: number | null;
	homeTeamSpectatorsReason: string | null;
	awayTeamCoaches: number | null;
	awayTeamCoachesReason: string | null;
	awayTeamPlayers: number | null;
	awayTeamPlayersReason: string | null;
	awayTeamSpectators: number | null;
	awayTeamSpectatorsReason: string | null;
	fieldCondition: number | null;
	fieldConditionReason: string | null;
	facilityCondition: number | null;
	facilityConditionReason: string | null;
	notes: string | null;
	status: MatchReportStatus | null;
	createdOn: ISODateTime | null;
	updatedOn: ISODateTime | null;
}

export interface ClientPortalDashboard {
	profile?: ClientPortalProfile | null;
	invoices?: ClientPortalInvoice[] | null;
	upcomingMatches?: ClientPortalMatch[] | null;
	recentMatches?: ClientPortalMatch[] | null;
}

interface ClientPortalStoreState {
	cacheEpoch: number;
	profileCache: ClientPortalProfile | null;
	profileLoadedAt: number | null;
	profileFetchPromise: Promise<ClientPortalProfile> | null;
	profileError: unknown;
	dashboard: ClientPortalDashboard | null;
	dashboardLoadedAt: number | null;
	dashboardFetchPromise: Promise<ClientPortalDashboard> | null;
	dashboardError: unknown;
	invoiceDetails: Record<string, ClientPortalInvoiceDetail>;
	invoiceDetailLoadedAt: Record<string, number>;
	invoiceDetailFetchPromises: Record<string, Promise<ClientPortalInvoiceDetail> | null>;
	matchReports: Record<string, ClientPortalMatchReport>;
	matchReportLoadedAt: Record<string, number>;
	matchReportFetchPromises: Record<string, Promise<ClientPortalMatchReport> | null>;
}

export const useClientPortalStore = defineStore('clientPortalStore', {
	state: (): ClientPortalStoreState => ({
		cacheEpoch: 0,
		profileCache: null,
		profileLoadedAt: null,
		profileFetchPromise: null,
		profileError: null,
		dashboard: null,
		dashboardLoadedAt: null,
		dashboardFetchPromise: null,
		dashboardError: null,
		invoiceDetails: {},
		invoiceDetailLoadedAt: {},
		invoiceDetailFetchPromises: {},
		matchReports: {},
		matchReportLoadedAt: {},
		matchReportFetchPromises: {}
	}),
	getters: {
		profile: (state) => state.profileCache ?? state.dashboard?.profile ?? null,
		invoices: (state) => state.dashboard?.invoices ?? [],
		upcomingMatches: (state) => state.dashboard?.upcomingMatches ?? [],
		recentMatches: (state) => state.dashboard?.recentMatches ?? []
	},
	actions: {
		async fetchProfile(options: { force?: boolean } = {}): Promise<ClientPortalProfile> {
			const now = Date.now();
			if (!options.force && this.profileCache && this.profileLoadedAt && now - this.profileLoadedAt < CLIENT_SURFACE_DASHBOARD_TTL_MS) {
				return this.profileCache;
			}

			if (this.profileFetchPromise) {
				return this.profileFetchPromise;
			}

			const epoch = this.cacheEpoch;
			this.profileFetchPromise = (async () => {
				try {
					const { data } = await apiClient.get<ClientPortalProfile>('/client-portal/me');
					const profile = data;
					if (epoch === this.cacheEpoch) {
						this.profileCache = profile;
						this.profileLoadedAt = Date.now();
						this.profileError = null;
						return this.profileCache;
					}
					return profile;
				} catch (error) {
					if (epoch === this.cacheEpoch) {
						this.profileCache = null;
						this.profileLoadedAt = null;
						this.profileError = error;
					}
					throw error;
				}
			})();

			try {
				return await this.profileFetchPromise;
			} finally {
				this.profileFetchPromise = null;
			}
		},
		async fetchDashboard(options: { force?: boolean } = {}): Promise<ClientPortalDashboard> {
			const now = Date.now();
			if (!options.force && this.dashboard && this.dashboardLoadedAt && now - this.dashboardLoadedAt < CLIENT_SURFACE_DASHBOARD_TTL_MS) {
				return this.dashboard;
			}

			if (this.dashboardFetchPromise) {
				return this.dashboardFetchPromise;
			}

			const epoch = this.cacheEpoch;
			this.dashboardFetchPromise = (async () => {
				try {
					await waitForRecentlyActivatedClientAccess();
					const { data } = await apiClient.get<ClientPortalDashboard>('/client-portal/dashboard');
					const dashboard = {
						profile: data.profile ?? null,
						invoices: data.invoices ?? [],
						upcomingMatches: data.upcomingMatches ?? [],
						recentMatches: data.recentMatches ?? []
					};
					if (epoch === this.cacheEpoch) {
						this.dashboard = dashboard;
						this.profileCache = dashboard.profile ?? null;
						this.profileLoadedAt = Date.now();
						this.dashboardLoadedAt = Date.now();
						this.dashboardError = null;
						this.profileError = null;
						return this.dashboard;
					}
					return dashboard;
				} catch (error) {
					if (epoch === this.cacheEpoch) {
						this.dashboard = null;
						this.dashboardLoadedAt = null;
						this.dashboardError = error;
					}
					throw error;
				}
			})();

			try {
				return await this.dashboardFetchPromise;
			} finally {
				this.dashboardFetchPromise = null;
			}
		},
		async fetchInvoiceDetail(batchId: string | number, options: { force?: boolean } = {}): Promise<ClientPortalInvoiceDetail> {
			const key = String(batchId);
			const now = Date.now();
			if (!options.force && this.invoiceDetails[key] && this.invoiceDetailLoadedAt[key] && now - this.invoiceDetailLoadedAt[key] < CLIENT_SURFACE_DETAIL_TTL_MS) {
				return this.invoiceDetails[key];
			}

			if (this.invoiceDetailFetchPromises[key]) {
				return this.invoiceDetailFetchPromises[key];
			}

			const epoch = this.cacheEpoch;
			this.invoiceDetailFetchPromises[key] = (async () => {
				const { data } = await apiClient.get<ClientPortalInvoiceDetail>(`/client-portal/invoices/${encodeURIComponent(key)}`);
				const detail = {
					...data,
					items: Array.isArray(data.items) ? data.items : [],
					reports: Array.isArray(data.reports) ? data.reports : []
				};
				if (epoch === this.cacheEpoch) {
					this.invoiceDetails[key] = detail;
					this.invoiceDetailLoadedAt[key] = Date.now();
					return this.invoiceDetails[key];
				}
				return detail;
			})();

			try {
				return await this.invoiceDetailFetchPromises[key];
			} finally {
				this.invoiceDetailFetchPromises[key] = null;
			}
		},
		async fetchMatchReport(matchNumber: string | number, options: { force?: boolean } = {}): Promise<ClientPortalMatchReport> {
			const key = String(matchNumber);
			const now = Date.now();
			if (!options.force && this.matchReports[key] && this.matchReportLoadedAt[key] && now - this.matchReportLoadedAt[key] < CLIENT_SURFACE_DETAIL_TTL_MS) {
				return this.matchReports[key];
			}

			if (this.matchReportFetchPromises[key]) {
				return this.matchReportFetchPromises[key];
			}

			const epoch = this.cacheEpoch;
			this.matchReportFetchPromises[key] = (async () => {
				const { data } = await apiClient.get<ClientPortalMatchReport>(`/client-portal/matches/${encodeURIComponent(key)}/report`);
				if (epoch === this.cacheEpoch) {
					this.matchReports[key] = data;
					this.matchReportLoadedAt[key] = Date.now();
					return this.matchReports[key];
				}
				return data;
			})();

			try {
				return await this.matchReportFetchPromises[key];
			} finally {
				this.matchReportFetchPromises[key] = null;
			}
		},
		async createBillingPortalSession(): Promise<ClientPortalBillingPortalSession> {
			const { data } = await apiClient.post<ClientPortalBillingPortalSession>('/client-portal/billing/portal-session');
			const session = data as ClientPortalBillingPortalSession | null | undefined;
			if (!session?.url) {
				throw new Error('Billing portal session was not returned.');
			}
			return session;
		},
		clearDashboard(): void {
			this.cacheEpoch += 1;
			this.profileCache = null;
			this.profileLoadedAt = null;
			this.profileFetchPromise = null;
			this.profileError = null;
			this.dashboard = null;
			this.dashboardLoadedAt = null;
			this.dashboardFetchPromise = null;
			this.dashboardError = null;
			this.invoiceDetails = {};
			this.invoiceDetailLoadedAt = {};
			this.invoiceDetailFetchPromises = {};
			this.matchReports = {};
			this.matchReportLoadedAt = {};
			this.matchReportFetchPromises = {};
		}
	}
});
